import { RollupCalculationSettings, RollupAggregation, getBacklogFilterNameById } from "WorkItemTracking/Scripts/Utils/RollupHelpers";
import { RollupQuery, RollupQueryWorkItemResult, WorkItemsRecency, RollupQueryResult } from "Analytics/Boards/RollupQueries/RollupQuery";
import { WorkItemStore } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import * as Resources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import * as Q from "q";
import { getService } from "VSS/Service";
import { WebPageDataService } from "VSS/Contributions/Services";
import { getDefaultWebContext } from "VSS/Context";
import * as TelemetryServices from "VSS/Telemetry/Services";
import { getLWPModule } from "VSS/LWP";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { RollupTimeoutRegistrySettings } from "./DataProviderContracts";

interface OpStoreWorkItemsRecency {
    workItemsChangedDate?: Date;
    workItemLinksChangedDate?: Date;
}

export interface RollupProgressBar {
    completedWorkCount: number;
    totalWorkCount: number;
    errorMessage?: string;
}

export class RollupDataProvider {
    private readonly dataProviderId = "ms.vss-work-web.backlog-rollup-registry-settings-data-provider";
    private pageSize = 200;

    private workItemIdToRollupData: { [workItemId: number]: RollupProgressBar };
    private workItemIdToPageDataPromise: { [workItemId: number]: IPromise<RollupQueryWorkItemResult[]> };
    private workItemStore: WorkItemStore;
    private interactiveMode = false;

    // Global debouncing handler for this data provider
    private timerHandle: number;
    // Global debouncing handler for telemetry tracking the number of data requests made for the page.
    private telemetryTimerHandle: number;
    // Int for tracking the highest page index that has been requested in the current work item id set's ax request paging.
    private requestedPageIndicies: number[];
    // An array of work item IDs and waiting promise resolves. The array index is page number.
    // This is used to debounce requests to the page for interactive mode
    // For non-interactive we return the data promise directly, without waiting
    private resolvesByPage: {
        workItemIds: number[];
        resolves?: ((value?: RollupQueryWorkItemResult[] | PromiseLike<RollupQueryWorkItemResult[]>) => void)[];
    }[] = [];
    /** Time to wait before determining the number of requests that were made to Analytics for a given grid load. */
    public debounceTimeRequestCountTelemetryMs = 1000;
    /** Time to wait betwen interactive data modifications before attempting to query Analytics */
    public debounceTimeGetDataMs = 10000;
    /** Time to wait between retries in case data in AX is bad quality (i.e. delayed) */
    public badQualityRetryMs = 10000;
    /** Number of retries to make when trying to retrieve quality data */
    public badQualityRetryAttempts = 4;

    /** Internal tracking of op store recency information */
    private witRecency: WorkItemsRecency;
    /** Publically available information on how recent Analytics data is */
    public axRecency: WorkItemsRecency;

    private syncDatesPromise: PromiseLike<any>;

    constructor(
        private readonly rollupMetric: RollupCalculationSettings,
        private workItemIds: number[],
        private backlogFilterWorkItemTypes?: string[]
    ) {
        this.resetWorkItemList(this.workItemIds);
        const service = getService(WebPageDataService, getDefaultWebContext());
        const registrySettings = service.getPageData<RollupTimeoutRegistrySettings>(this.dataProviderId);
        if (registrySettings) {
            this.badQualityRetryAttempts = registrySettings.badQualityRetryAttempts;
            this.badQualityRetryMs = registrySettings.badQualityRetryMs;
            this.debounceTimeGetDataMs = registrySettings.debounceTimeGetDataMs;
            this.pageSize = registrySettings.pageSize;
        } else if (FeatureAvailabilityService.isFeatureEnabled("WebAccess.Agile.Backlog.EnableDecreasedRollupRetryTimes", false)) {
            this.badQualityRetryAttempts = 20;
            this.badQualityRetryMs = 2000;
        }
    }

    public resetWorkItemList(workItemIds: number[]): void {
        if (this.timerHandle) {
            clearTimeout(this.timerHandle);
        }
        if (this.telemetryTimerHandle) {
            clearTimeout(this.telemetryTimerHandle);
        }
        this.requestedPageIndicies = [];
        this.resolvesByPage = [];

        this.workItemIdToRollupData = {};
        this.workItemIdToPageDataPromise = {};

        this._initializeWorkItemIdMaps(workItemIds);
    }

    public checkAndUpdateWorkItemIds(updatedWorkItemIds: number[]): number[] {
        // if more work item ids have been paged in, update with the new ids
        const workItemIdsToInitialize = [];
        updatedWorkItemIds.forEach(updatedWorkItemId => {
            if (this.workItemIdToRollupData[updatedWorkItemId] === undefined) {
                workItemIdsToInitialize.push(updatedWorkItemId);
            }
        });

        this._initializeWorkItemIdMaps(workItemIdsToInitialize);
        this.workItemIds = updatedWorkItemIds;

        // return new workItemIds value for testing
        return this.workItemIds;
    }

    public setStore(store: WorkItemStore) {
        this.workItemStore = store;
    }

    public resetRollupData(workItemResetUtcTimestamp: number, workItemLinksResetUtcTimestamp: number): void {
        Object.keys(this.workItemIdToRollupData).forEach(key => (this.workItemIdToRollupData[key] = null));
        Object.keys(this.workItemIdToPageDataPromise).forEach(key => (this.workItemIdToPageDataPromise[key] = null));
        // If we are called, this means user was interacting with the page and needed to re-query the data
        // This controls debouncing - for initial load, there's none.
        this.interactiveMode = true;
        // Since user is interacting, we are also going to move WIT time forward
        if (!this.witRecency) {
            // If user started interacting before we got rollup data, we'll have to set this data initially
            this.witRecency = {};
        }
        if (workItemResetUtcTimestamp) {
            this.witRecency.workItemsChangedUtcTimestamp = workItemResetUtcTimestamp;
        }

        if (workItemLinksResetUtcTimestamp) {
            this.witRecency.workItemLinksChangedUtcTimestamp = workItemLinksResetUtcTimestamp;
        }

        // Kill a timer if there is one - we need to wait more for AX to catch up
        if (this.timerHandle) {
            clearTimeout(this.timerHandle);
        }
        if (this.telemetryTimerHandle) {
            clearTimeout(this.telemetryTimerHandle);
        }
        this.requestedPageIndicies = [];
        this.syncDatesPromise = undefined;
    }

    public async getRollupData(workItemId: number, interactive = false): Promise<RollupProgressBar> {
        // For temporary work items that were just created but not saved yet return empty data
        // Note: this will be re-queried from UI when item gets actual ID
        if (workItemId <= 0) {
            return {
                completedWorkCount: 0,
                totalWorkCount: 0
            };
        }

        this._throwIfWorkItemIdNotPresent(workItemId);
        if (!this.workItemIdToRollupData[workItemId]) {
            let pageDataPromise = this.workItemIdToPageDataPromise[workItemId];
            if (!pageDataPromise) {
                pageDataPromise = this._loadPageData(workItemId);
            }

            try {
                const rollupProgressData: RollupQueryWorkItemResult[] = await pageDataPromise;
                // When null is returned, this designates bad data quality - so setting the work item
                // data to null too and passing it up
                if (rollupProgressData) {
                    rollupProgressData.forEach(rollupQueryResult => {
                        this.workItemIdToRollupData[rollupQueryResult.WorkItemId] = this._interpretData(rollupQueryResult);
                    });
                } else {
                    this.workItemIdToRollupData[workItemId] = null;
                }
            } catch (e) {
                if (e.error) {
                    this.workItemIdToRollupData[workItemId] = {
                        completedWorkCount: 0,
                        totalWorkCount: 0,
                        errorMessage: e.error.message
                    };
                } else {
                    throw e;
                }
            }
        }

        return this.workItemIdToRollupData[workItemId];
    }

    public getTooltipLabel(): IPromise<string> {
        const deferred = Q.defer<string>();
        switch (this.rollupMetric.aggregation) {
            case RollupAggregation.Sum:
                if (this.workItemStore) {
                    this.workItemStore
                        .beginGetFields()
                        .then(() => {
                            const aggregationFieldDefinition = this.workItemStore.getFieldDefinition(this.rollupMetric.aggregationField);
                            if (aggregationFieldDefinition) {
                                deferred.resolve(aggregationFieldDefinition.name);
                            } else {
                                deferred.resolve("");
                            }
                        })
                        .catch(() => deferred.resolve(""));
                }
                break;
            case RollupAggregation.Count:
            default:
                let tooltipLabel = Resources.WorkItemsText;
                if (this.rollupMetric.workItemTypeFilter) {
                    tooltipLabel = this.rollupMetric.workItemTypeFilter;
                } else if (this.rollupMetric.backlogFilter) {
                    tooltipLabel = getBacklogFilterNameById(this.rollupMetric.backlogFilter);
                }
                deferred.resolve(tooltipLabel);
                break;
        }

        return deferred.promise;
    }

    public getRollupMetric(): RollupCalculationSettings {
        return this.rollupMetric;
    }

    private _interpretData(rollupQueryResult: RollupQueryWorkItemResult): RollupProgressBar {
        const descendantDetails = rollupQueryResult.Descendants;
        let completedWorkCount = 0;
        let totalWorkCount = 0;
        descendantDetails.forEach(details => {
            if (details && (details.Count || details.Sum)) {
                if (details.isCompleted) {
                    completedWorkCount += details.Count || details.Sum;
                }
                totalWorkCount += details.Count || details.Sum;
            }
        });
        return {
            completedWorkCount: Math.round(completedWorkCount*10)/10,
            totalWorkCount: Math.round(totalWorkCount*10)/10
        };
    }

    private _throwIfWorkItemIdNotPresent(workItemId: number) {
        // We initialize the work items we were given in workItemIdToRollupData as null
        // If we find that it is undefined then we never received that workItemId
        if (this.workItemIdToRollupData[workItemId] === undefined) {
            throw new Error(Resources.CannotFindRollupData);
        }
    }

    private _getPageIndex(workItemId: number): number {
        const workItemIndex = this.workItemIds.indexOf(workItemId);
        return Math.floor(workItemIndex / this.pageSize);
    }

    private _getPageInformation(workItemId: number): { pageIndex: number; workItemIds: number[] } {
        const pageIndex = this._getPageIndex(workItemId);
        const pageStartWorkItemIdIndex = pageIndex * this.pageSize;

        return {
            pageIndex: pageIndex,
            workItemIds: this.workItemIds.slice(pageStartWorkItemIdIndex, pageStartWorkItemIdIndex + this.pageSize)
        };
    }

    public getWitRecencyData(workItemIds: number[]): IPromise<WorkItemsRecency> {
        const service = getService(WebPageDataService, getDefaultWebContext());
        const dataProviderId = "ms.vss-work-web.recency-workitem-data-provider";

        if (!this.witRecency) {
            const opStoreRecencyPromise = service.getDataAsync<OpStoreWorkItemsRecency>(dataProviderId, /*serviceInstanceId*/ null);

            return opStoreRecencyPromise.then(data => {
                // Note that op store and data provider returns data in UTC, although data provider parses it as local time.
                // We take timestamp anyways, so the time zone doesn't matter from here on
                const utcOpStoreDates: WorkItemsRecency = {
                    workItemsChangedUtcTimestamp: data.workItemsChangedDate ? data.workItemsChangedDate.getTime() : null,
                    workItemLinksChangedUtcTimestamp: data.workItemLinksChangedDate ? data.workItemLinksChangedDate.getTime() : null
                };

                this.witRecency = utcOpStoreDates;
                return this.witRecency;
            });
        } else {
            return Promise.resolve(this.witRecency);
        }
    }

    /** Start delayed query, exposed as public for stubbing */
    public runQueryDelayed(pageWorkItemIds: number[], retriesMade: number, initialStartTime: number): Promise<RollupQueryWorkItemResult[]> {
        return new Promise<RollupQueryWorkItemResult[]>(resolve => {
            setTimeout(() => {
                resolve(this.runQuery(pageWorkItemIds, retriesMade + 1, initialStartTime, true));
            }, this.badQualityRetryMs);
        });
    }

    public isAnalyticsCaughtUp(axDates: WorkItemsRecency, opStoreDates: WorkItemsRecency): boolean {
        // Check that there's a match in data - if one of dates is null, the other one should also be null
        if (
            (axDates.workItemLinksChangedUtcTimestamp == null && opStoreDates.workItemLinksChangedUtcTimestamp != null) ||
            (axDates.workItemLinksChangedUtcTimestamp != null && opStoreDates.workItemLinksChangedUtcTimestamp == null) ||
            (axDates.workItemsChangedUtcTimestamp == null && opStoreDates.workItemsChangedUtcTimestamp != null) ||
            (axDates.workItemsChangedUtcTimestamp != null && opStoreDates.workItemsChangedUtcTimestamp == null)
        ) {
            return false;
        }

        if (
            // Check that AX is ahead, or both op store and AX have no data
            (axDates.workItemLinksChangedUtcTimestamp >= opStoreDates.workItemLinksChangedUtcTimestamp ||
                (axDates.workItemLinksChangedUtcTimestamp == null && opStoreDates.workItemLinksChangedUtcTimestamp == null)) &&
            // Check that AX is ahead, or both op store and AX have no data
            (axDates.workItemsChangedUtcTimestamp >= opStoreDates.workItemsChangedUtcTimestamp ||
                (axDates.workItemsChangedUtcTimestamp == null && opStoreDates.workItemsChangedUtcTimestamp == null))
        ) {
            return true;
        } else {
            return false;
        }
    }

    /** A failsafe instance of runQuery's old implementation for FF off scenarios. implementation based on commit: a43a5f6649dddc0a0a930b8f54b4843e7fd6d283 */
    private _runQueryDeprecated(pageWorkItemIds: number[], retriesMade: number, initialStartTime?: number): IPromise<RollupQueryWorkItemResult[]> {
        const project = getDefaultWebContext().project;

        const witRecencyQuery = this.getWitRecencyData(pageWorkItemIds);
        const startTime = Date.now();

        const legacyModule = getLWPModule("VSS/Legacy/Legacy");
        if (!legacyModule) {
            console && console.warn("`RollupQuery` requires the `VSS/Legacy/Legacy` module");
        }
        const pageContext = legacyModule ? legacyModule.getLWPComponentProps().pageContext : undefined;

        const dataPromise = witRecencyQuery
            .then(witRecency => {
                const rollupQuery = new RollupQuery(pageWorkItemIds, this.rollupMetric, project.id, this.backlogFilterWorkItemTypes, [
                    {
                        providerTableName: "WorkItemRevision",
                        ExpectedSyncDate: new Date(witRecency.workItemsChangedUtcTimestamp)
                    },
                    {
                        providerTableName: "WorkItemLink",
                        ExpectedSyncDate: new Date(witRecency.workItemLinksChangedUtcTimestamp)
                    }
                ]);
                return rollupQuery.runQuery(pageContext).then(rollupData => {
                    const now = Date.now();
                    const telemetryData: TelemetryServices.TelemetryEventData = {
                        area: "Agile",
                        feature: "RollupDataProvider",
                        elapsedTime: now - startTime,
                        properties: {
                            caughtUp: true,
                            axRecency: rollupData.syncDates,
                            opStoreRecency: witRecency,
                            retriesMade: retriesMade,
                            interactiveMode: this.interactiveMode,
                            totalElapsedTime: now - (initialStartTime || startTime)
                        }
                    };

                    if (this.isAnalyticsCaughtUp(rollupData.syncDates, witRecency)) {
                        this.axRecency = rollupData.syncDates;
                        TelemetryServices.publishEvent(telemetryData);
                        return rollupData.results;
                    } else {
                        if (retriesMade < this.badQualityRetryAttempts) {
                            // Recursively call ourselves every 10 seconds and see if we can get updated data
                            // This attempts to see if better quality data is available, and then pulls data if it is
                            return this.runQueryDelayed(pageWorkItemIds, retriesMade, initialStartTime || startTime);
                        } else {
                            this.axRecency = rollupData.syncDates;
                            telemetryData.properties.caughtUp = false;
                            TelemetryServices.publishEvent(telemetryData);
                            return null;
                        }
                    }
                });
            })
            .then(rollupData => {
                pageWorkItemIds.forEach(pageWorkItemId => {
                    this.workItemIdToPageDataPromise[pageWorkItemId] = null;
                });
                return rollupData;
            });

        pageWorkItemIds.forEach(pageWorkItemId => {
            // When this gets called via recursion this will replace first promise with most recent one
            // so that new calls to getRollupData will receive the last data promise made
            // but previous calls will wait for the chain constructed in the above setTimeout call
            this.workItemIdToPageDataPromise[pageWorkItemId] = dataPromise;
        });

        return dataPromise;
    }

    /** Common query runner
     * Start query for a set of work items and store the resulting promise in workItemIdToPageDataPromise map
     */
    private runQuery(pageWorkItemIds: number[], retriesMade: number, initialStartTime?: number, isRetry?: boolean): IPromise<RollupQueryWorkItemResult[]> {
        const useNewQueryStrategy = FeatureAvailabilityService.isFeatureEnabled("WebAccess.Agile.Backlog.EnableRollupSyncDatesConditionals", false);
        if (!useNewQueryStrategy) {
            return this._runQueryDeprecated(pageWorkItemIds, retriesMade, initialStartTime);
        } else {
            const project = getDefaultWebContext().project;

            const witRecencyQuery = this.getWitRecencyData(pageWorkItemIds);
            const startTime = Date.now();

            const legacyModule = getLWPModule("VSS/Legacy/Legacy");
            if (!legacyModule) {
                console && console.warn("`RollupQuery` requires the `VSS/Legacy/Legacy` module");
            }
            const pageContext = legacyModule ? legacyModule.getLWPComponentProps().pageContext : undefined;

            const dataPromise = witRecencyQuery
                .then(witRecency => {
                    const rollupQuery = new RollupQuery(pageWorkItemIds, this.rollupMetric, project.id, this.backlogFilterWorkItemTypes, [
                        {
                            providerTableName: "WorkItemRevision",
                            ExpectedSyncDate: new Date(witRecency.workItemsChangedUtcTimestamp)
                        },
                        {
                            providerTableName: "WorkItemLink",
                            ExpectedSyncDate: new Date(witRecency.workItemLinksChangedUtcTimestamp)
                        }
                    ]);

                    const processQueryResults = (enableRetry) => (rollupData: RollupQueryResult) => {
                        const now = Date.now();
                        const telemetryData: TelemetryServices.TelemetryEventData = {
                            area: "Agile",
                            feature: "RollupDataProvider",
                            elapsedTime: now - startTime,
                            properties: {
                                caughtUp: true,
                                axRecency: rollupData.syncDates,
                                opStoreRecency: witRecency,
                                retriesMade: retriesMade,
                                interactiveMode: this.interactiveMode,
                                totalElapsedTime: now - (initialStartTime || startTime)
                            }
                        };

                        if (this.isAnalyticsCaughtUp(rollupData.syncDates, witRecency)) {
                            this.axRecency = rollupData.syncDates;
                            TelemetryServices.publishEvent(telemetryData);
                            return rollupData.results;
                        } else {
                            if (retriesMade < this.badQualityRetryAttempts && enableRetry) {
                                // Recursively call ourselves every 10 seconds and see if we can get updated data
                                // This attempts to see if better quality data is available, and then pulls data if it is
                                return this.runQueryDelayed(pageWorkItemIds, retriesMade, initialStartTime || startTime);
                            } else {
                                this.axRecency = rollupData.syncDates;
                                telemetryData.properties.caughtUp = false;
                                TelemetryServices.publishEvent(telemetryData);
                                return null;
                            }
                        }
                    };

                    if (isRetry) {
                        return rollupQuery.runQuery(pageContext).then(processQueryResults(true));
                    }
                    if (this.syncDatesPromise == undefined) {
                        this.syncDatesPromise = rollupQuery.runQuery(pageContext).then(processQueryResults(true));
                        return this.syncDatesPromise;
                    } else {
                        return this.syncDatesPromise.then(() => {
                            return rollupQuery.runQuery(pageContext).then(processQueryResults(false));
                        });
                    }
                })
                .then(rollupData => {
                    pageWorkItemIds.forEach(pageWorkItemId => {
                        this.workItemIdToPageDataPromise[pageWorkItemId] = null;
                    });
                    return rollupData;
                });

            pageWorkItemIds.forEach(pageWorkItemId => {
                // When this gets called via recursion this will replace first promise with most recent one
                // so that new calls to getRollupData will receive the last data promise made
                // but previous calls will wait for the chain constructed in the above setTimeout call
                this.workItemIdToPageDataPromise[pageWorkItemId] = dataPromise;
            });

            return dataPromise;
        }
    }

    private _loadPageData(workItemId: number): IPromise<RollupQueryWorkItemResult[]> {
        const pageInformation = this._getPageInformation(workItemId);

        // If interactive flag is set, debounce all requests to a data page
        if (this.interactiveMode) {
            // Wait 10 seconds for AX to catch up
            const timer = setTimeout(() => {
                // For each page that's waiting, run the query and resolve the awaiters
                this.resolvesByPage.forEach(pageResolves => {
                    const result = this.runQuery(pageResolves.workItemIds, 0);
                    // Resolve each callback in resolves array
                    pageResolves.resolves.forEach(async resolve => {
                        resolve(result);
                    });
                });
                this._recordRequestCountTelemetry(this.resolvesByPage.length);
                this.resolvesByPage = [];
                this.timerHandle = null;
            }, this.debounceTimeGetDataMs);

            if (this.timerHandle) {
                clearTimeout(this.timerHandle);
            }
            this.timerHandle = timer;

            // Return new promise for each call
            return new Promise<RollupQueryWorkItemResult[]>(resolve => {
                // The promise resolve callback is stored in resolvesByPage, which will be fulfilled above after setTimeout succeeeds
                if (!this.resolvesByPage[pageInformation.pageIndex]) {
                    this.resolvesByPage[pageInformation.pageIndex] = {
                        workItemIds: pageInformation.workItemIds,
                        resolves: []
                    };
                }

                this.resolvesByPage[pageInformation.pageIndex].resolves.push(resolve);
            });
        } else {
            // If we dont receive a new index value request then let the first timer go forth.
            if (this.requestedPageIndicies.indexOf(pageInformation.pageIndex) === -1) {
                this.requestedPageIndicies.push(pageInformation.pageIndex);
                // We have a new index that was requested. debounce the timeout.
                if (this.telemetryTimerHandle) {
                    clearTimeout(this.telemetryTimerHandle);
                }
                this.telemetryTimerHandle = setTimeout(() => {
                    if (this.requestedPageIndicies.length) {
                        this._recordRequestCountTelemetry(this.requestedPageIndicies.length);
                    }
                }, this.debounceTimeRequestCountTelemetryMs);
            }
            return this.runQuery(pageInformation.workItemIds, 0);
        }
    }

    private _initializeWorkItemIdMaps(workItemIds: number[]) {
        workItemIds.forEach(workItemId => {
            this.workItemIdToRollupData[workItemId] = null;
            this.workItemIdToPageDataPromise[workItemId] = null;
        });
    }

    /**
     * Records the number of pages of that were requested from Analytics.
     * @param requestCount the number of requests.
     */
    private _recordRequestCountTelemetry(requestCount: number): void {
        TelemetryServices.publishEvent(
            new TelemetryServices.TelemetryEventData(
                "Agile", // Area
                "RollupDataProvider-RequestCountReport", // Feature
                {
                    // Properties
                    interactiveMode: this.interactiveMode,
                    requestCount
                }
            )
        );
    }
}
